import {useCallback} from 'react';
import {useServiceProviders} from './useServiceProviders';
import {useServiceProviderControl} from './useServiceProviderControl';
import {useServiceProvidersUIStore} from '../store/serviceProvidersUIStore';
import {useNotification} from '../../../Services/useNotification';
import {ServiceProvider} from '../models/serviceProvider.model';

/**
 * Unified hook for service providers management that combines data and UI state
 * This replaces the need for multiple useState calls in components
 */
export const useServiceProvidersManagement = () => {
  // Data hooks
  const serviceProvidersHook = useServiceProviders();
  const serviceProviderControl = useServiceProviderControl();
  const uiStore = useServiceProvidersUIStore();
  const {success: showSuccess} = useNotification();

  // Destructure from serviceProvidersHook
  const {
    serviceProviders, pagination, loading, error,
    handlePageChange, handlePageSizeChange, refetch
  } = serviceProvidersHook;

  // Destructure from serviceProviderControl
  const {deleteProviderById} = serviceProviderControl;

  // Destructure from uiStore
  const {
    showAddForm, showUpdateForm, showDetailsModal,
    selectedProviderId, selectedProvider,
    openAddForm, closeAddForm, openUpdateForm, closeUpdateForm,
    openDetailsModal, closeDetailsModal,
    addFormData, addFormErrors, addFormLoading, addFormShowPassword,
    setAddFormData, setAddFormErrors, setAddFormLoading, setAddFormShowPassword, resetAddForm,
    updateFormData, updateFormErrors, updateFormLoading,
    setUpdateFormData, setUpdateFormErrors, setUpdateFormLoading, resetUpdateForm,
    reset: resetUI
  } = uiStore;

  // Unified Actions
  const handleAddSuccess = useCallback(() => {
    closeAddForm();
    refetch();
  }, [closeAddForm, refetch]);

  const handleViewDetails = useCallback((id: string) => {
    openDetailsModal(id);
  }, [openDetailsModal]);

  const handleCloseDetails = useCallback(() => {
    closeDetailsModal();
  }, [closeDetailsModal]);

  const handleEdit = useCallback((provider: ServiceProvider) => {
    openUpdateForm(provider);
  }, [openUpdateForm]);

  const handleUpdateSuccess = useCallback(() => {
    closeUpdateForm();
    refetch();
  }, [closeUpdateForm, refetch]);

  const handleDelete = useCallback(async (id: string) => {
    try {
      await deleteProviderById(id);
      showSuccess('تم حذف مزود الخدمة بنجاح');
      refetch();
      return true;
    } catch (error) {
      return false;
    }
  }, [deleteProviderById, refetch]);

  const handleAddFormSubmit = useCallback(async (formData: any) => {
    setAddFormLoading(true);
    try {
      // This would be handled by the actual creation logic
      // For now, we'll just simulate success
      showSuccess('تم إضافة مزود الخدمة بنجاح');
      resetAddForm();
      return true;
    } catch (error) {
      return false;
    } finally {
      setAddFormLoading(false);
    }
  }, [setAddFormLoading, resetAddForm]);

  const handleUpdateFormSubmit = useCallback(async (formData: any) => {
    setUpdateFormLoading(true);
    try {
      // This would be handled by the actual update logic
      // For now, we'll just simulate success
      showSuccess('تم تحديث مزود الخدمة بنجاح');
      resetUpdateForm();
      return true;
    } catch (error) {
      return false;
    } finally {
      setUpdateFormLoading(false);
    }
  }, [setUpdateFormLoading, resetUpdateForm]);

  const handleInputChange = useCallback((formType: 'add' | 'update', field: string, value: any) => {
    if (formType === 'add') {
      setAddFormData({[field]: value});
      // Clear error when user starts typing
      if (addFormErrors[field]) {
        setAddFormErrors({...addFormErrors, [field]: ''});
      }
    } else {
      setUpdateFormData({[field]: value});
      // Clear error when user starts typing
      if (updateFormErrors[field]) {
        setUpdateFormErrors({...updateFormErrors, [field]: ''});
      }
    }
  }, [setAddFormData, setUpdateFormData, addFormErrors, updateFormErrors, setAddFormErrors, setUpdateFormErrors]);

  // Reset all stores
  const resetAllStores = useCallback(() => {
    resetUI();
  }, [resetUI]);

  return {
    // UI State
    showAddForm, showUpdateForm, showDetailsModal,
    selectedProviderId, selectedProvider,

    // Add Form State
    addFormData, addFormErrors, addFormLoading, addFormShowPassword,

    // Update Form State
    updateFormData, updateFormErrors, updateFormLoading,

    // Data
    serviceProviders, pagination,

    // Loading States
    loading,

    // Error States
    error,

    // Actions
    handleAddSuccess, handleViewDetails, handleCloseDetails,
    handleEdit, handleUpdateSuccess, handleDelete,
    handleAddFormSubmit, handleUpdateFormSubmit, handleInputChange,
    handlePageChange, handlePageSizeChange, refetch, resetAllStores,

    // Modal Actions
    openAddForm, closeAddForm, openUpdateForm, closeUpdateForm,
    openDetailsModal, closeDetailsModal,

    // Form Actions
    setAddFormData, setAddFormErrors, setAddFormLoading, setAddFormShowPassword, resetAddForm,
    setUpdateFormData, setUpdateFormErrors, setUpdateFormLoading, resetUpdateForm
  };
};
